import {
  Activity,
  Check,
  CheckCircle2,
  CircleAlert,
  Clock3,
  ExternalLink,
  LoaderCircle,
  Search,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  acknowledgeIncident,
  diagnoseIncident,
  requestIncidentRemediation,
  validateIncidentFallback,
} from "../api";
import { formatRelativeTime } from "../lib/format";
import type {
  IncidentItem,
  IncidentDiagnosisResponse,
  FallbackValidation,
  WorkspaceCapabilities,
} from "../types";

type IncidentsViewProps = {
  capabilities: WorkspaceCapabilities;
  incidents: IncidentItem[];
  workspaceId: string;
  onIncidentChanged: (incident: IncidentItem) => void;
};

const severityLabels: Record<IncidentItem["severity"], string> = {
  low: "低",
  medium: "中",
  high: "高",
  critical: "严重",
};

const statusLabels: Record<IncidentItem["status"], string> = {
  open: "待接管",
  acknowledged: "处理中",
  resolved: "已解决",
};

function evidenceText(incident: IncidentItem): string {
  return (
    incident.evidence.observed ??
    incident.evidence.signal ??
    incident.evidence.source ??
    "已记录运行异常信号"
  );
}

export function IncidentsView({
  capabilities,
  incidents,
  workspaceId,
  onIncidentChanged,
}: IncidentsViewProps) {
  const [destinations, setDestinations] = useState<Record<string, string>>({});
  const [working, setWorking] = useState<string | null>(null);
  const [diagnoses, setDiagnoses] = useState<
    Record<string, IncidentDiagnosisResponse>
  >({});
  const [validations, setValidations] = useState<
    Record<string, FallbackValidation>
  >({});
  const [error, setError] = useState("");

  useEffect(() => {
    setDestinations({});
    setWorking(null);
    setDiagnoses({});
    setValidations({});
    setError("");
  }, [workspaceId]);

  const summary = useMemo(
    () => ({
      unresolved: incidents.filter((item) => item.status !== "resolved").length,
      highImpact: incidents.filter(
        (item) =>
          item.status !== "resolved" &&
          (item.severity === "high" || item.severity === "critical"),
      ).length,
      exposed: incidents
        .filter((item) => item.status !== "resolved")
        .reduce((total, item) => total + item.impact.deploymentQuantity, 0),
      pending: incidents.filter((item) => item.approvalStatus === "pending")
        .length,
    }),
    [incidents],
  );

  const acknowledge = async (incident: IncidentItem) => {
    setWorking(`ack:${incident.id}`);
    setError("");
    try {
      const response = await acknowledgeIncident(incident.id);
      onIncidentChanged(response.incident);
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "Incident 接管失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const requestRemediation = async (incident: IncidentItem) => {
    const destination = (destinations[incident.id] ?? "").trim();
    setWorking(`remediate:${incident.id}`);
    setError("");
    try {
      const response = await requestIncidentRemediation(
        incident.id,
        destination,
      );
      onIncidentChanged(response.incident);
      setDestinations((current) => ({ ...current, [incident.id]: "" }));
    } catch (actionError) {
      setError(
        actionError instanceof Error
          ? actionError.message
          : "修复审批提交失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const diagnose = async (incident: IncidentItem) => {
    setWorking(`diagnose:${incident.id}`);
    setError("");
    try {
      const response = await diagnoseIncident(incident.id);
      setDiagnoses((current) => ({ ...current, [incident.id]: response }));
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "诊断未能完成。",
      );
    } finally {
      setWorking(null);
    }
  };

  const validateFallback = async (incident: IncidentItem) => {
    const destination = (destinations[incident.id] ?? "").trim();
    setWorking(`validate:${incident.id}`);
    setError("");
    try {
      const response = await validateIncidentFallback(
        incident.id,
        destination,
      );
      setValidations((current) => ({
        ...current,
        [incident.id]: response.validation,
      }));
    } catch (actionError) {
      setValidations((current) => {
        const next = { ...current };
        delete next[incident.id];
        return next;
      });
      setError(
        actionError instanceof Error
          ? actionError.message
          : "fallback 预检失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="page incidents-page">
      <section className="page-heading">
        <div>
          <h1>Incidents</h1>
          <p>冻结现实影响，接管异常，并通过受控 Release 完成修复。</p>
        </div>
        <div className="incident-health">
          <Activity size={16} />
          {summary.unresolved
            ? `${summary.unresolved} 个事件需要响应`
            : "当前运行稳定"}
        </div>
      </section>

      <section className="incident-summary">
        <div>
          <CircleAlert size={17} />
          <span>未解决</span>
          <strong>{summary.unresolved}</strong>
        </div>
        <div>
          <ShieldCheck size={17} />
          <span>高影响</span>
          <strong>{summary.highImpact}</strong>
        </div>
        <div>
          <ExternalLink size={17} />
          <span>影响入口</span>
          <strong>{summary.exposed.toLocaleString("zh-CN")}</strong>
        </div>
        <div>
          <Clock3 size={17} />
          <span>等待审批</span>
          <strong>{summary.pending}</strong>
        </div>
      </section>

      {error ? <div className="action-error">{error}</div> : null}

      <section className="incident-table" aria-label="Incident 响应列表">
        <div className="incident-table-head">
          <span>事件 / Qode</span>
          <span>证据</span>
          <span>现实影响</span>
          <span>状态</span>
          <span>响应</span>
        </div>

        {!incidents.length ? (
          <div className="incident-empty">
            <CheckCircle2 size={22} />
            <strong>没有 Incident</strong>
            <span>运行异常被识别后会在这里形成可治理记录。</span>
          </div>
        ) : null}

        {incidents.map((incident) => {
          const destination = destinations[incident.id] ?? "";
          const isResolved = incident.status === "resolved";
          const approvalPending =
            incident.approvalStatus === "pending" ||
            incident.approvalStatus === "executing";
          const acknowledging = working === `ack:${incident.id}`;
          const remediating = working === `remediate:${incident.id}`;
          const diagnosing = working === `diagnose:${incident.id}`;
          const validating = working === `validate:${incident.id}`;
          const diagnosis = diagnoses[incident.id];
          const validation = validations[incident.id];
          const destinationValid =
            destination.trim().length > 0 &&
            destination.trim() !== incident.qode.destination;
          const fallbackValidated =
            validation?.status === "healthy" &&
            validation.target === destination.trim();

          return (
            <article className="incident-row" key={incident.id}>
              <div className="incident-identity">
                <div>
                  <span
                    className={`incident-severity incident-severity-${incident.severity}`}
                  >
                    {severityLabels[incident.severity]}
                  </span>
                  <small>{formatRelativeTime(incident.detectedAt)}</small>
                </div>
                <strong>{incident.title}</strong>
                <span>
                  {incident.qode.name} · /r/{incident.qode.slug}
                </span>
              </div>

              <div className="incident-evidence">
                <div className="incident-signal-line">
                  <span className={`signal-pill signal-${incident.sourceSignal}`}>
                    {incident.sourceSignal.toUpperCase()}
                  </span>
                  <strong>{evidenceText(incident)}</strong>
                </div>
                <span>{incident.summary}</span>
                <button
                  className="incident-diagnose"
                  type="button"
                  disabled={working !== null}
                  onClick={() => void diagnose(incident)}
                >
                  {diagnosing ? (
                    <LoaderCircle className="spin" size={13} />
                  ) : (
                    <Search size={13} />
                  )}
                  确定性诊断
                </button>
                {diagnosis ? (
                  <div className="incident-diagnosis">
                    <strong>{diagnosis.diagnosis.likelyCause}</strong>
                    <span>
                      {diagnosis.diagnosis.confidence === "high"
                        ? "高置信度"
                        : "中置信度"}
                      · {diagnosis.links.healthChecks.length} 条健康证据 ·{" "}
                      {diagnosis.links.auditEvents.length} 条审计记录
                    </span>
                  </div>
                ) : null}
              </div>

              <div className="incident-impact">
                <strong>
                  {incident.impact.deploymentQuantity.toLocaleString("zh-CN")}
                </strong>
                <span>
                  {incident.impact.deploymentBatches} 个批次 · 替换成本 ¥
                  {(incident.impact.replacementCostCents / 100).toLocaleString(
                    "zh-CN",
                  )}
                </span>
              </div>

              <div className="incident-status-cell">
                <span
                  className={`incident-status incident-status-${incident.status}`}
                >
                  {isResolved ? (
                    <CheckCircle2 size={13} />
                  ) : approvalPending ? (
                    <Clock3 size={13} />
                  ) : (
                    <CircleAlert size={13} />
                  )}
                  {approvalPending ? "等待审批" : statusLabels[incident.status]}
                </span>
                <small>
                  {isResolved && incident.resolvedAt
                    ? formatRelativeTime(incident.resolvedAt)
                    : incident.acknowledgedAt
                      ? `接管于 ${formatRelativeTime(incident.acknowledgedAt)}`
                      : "尚未接管"}
                </small>
                {!isResolved && incident.response.dueAt ? (
                  <small
                    className={
                      incident.response.overdue ? "incident-overdue" : ""
                    }
                  >
                    {incident.response.overdue
                      ? `响应已超时 · L${incident.response.escalationLevel}`
                      : `响应时限 ${formatRelativeTime(incident.response.dueAt)}`}
                  </small>
                ) : null}
                {!isResolved && incident.recovery.startedAt ? (
                  <small className="incident-recovery">
                    恢复观察 {incident.recovery.healthyObservations}/
                    {incident.recovery.requiredObservations}
                    {incident.recovery.readyAt ? " · 可确认关闭" : ""}
                  </small>
                ) : null}
              </div>

              <div className="incident-response">
                {isResolved ? (
                  <>
                    <strong>修复 Release 已激活</strong>
                    <span>{incident.recommendedDestination}</span>
                    <small>{incident.resolution}</small>
                  </>
                ) : approvalPending ? (
                  <>
                    <strong>修复方案已冻结</strong>
                    <span>{incident.recommendedDestination}</span>
                    <small>前往 Governance 由其他审批人处理</small>
                  </>
                ) : (
                  <>
                    {incident.status === "open" ? (
                      <button
                        className="secondary-button incident-acknowledge"
                        type="button"
                        disabled={!capabilities.operate || working !== null}
                        onClick={() => void acknowledge(incident)}
                      >
                        {acknowledging ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : (
                          <Check size={14} />
                        )}
                        接管
                      </button>
                    ) : null}
                    <div className="incident-remediation">
                      <input
                        type="url"
                        value={destination}
                        disabled={!capabilities.operate || working !== null}
                        placeholder="https://backup.example.com/path"
                        aria-label={`${incident.title} 修复目标`}
                        onChange={(event) =>
                          {
                            setDestinations((current) => ({
                              ...current,
                              [incident.id]: event.target.value,
                            }));
                            setValidations((current) => {
                              const next = { ...current };
                              delete next[incident.id];
                              return next;
                            });
                          }
                        }
                      />
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={
                          !capabilities.operate ||
                          working !== null ||
                          !destinationValid
                        }
                        onClick={() => void validateFallback(incident)}
                      >
                        {validating ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : (
                          <ShieldCheck size={14} />
                        )}
                        预检
                      </button>
                      <button
                        className="primary-button"
                        type="button"
                        disabled={
                          !capabilities.operate ||
                          working !== null ||
                          !destinationValid ||
                          !fallbackValidated
                        }
                        onClick={() => void requestRemediation(incident)}
                      >
                        {remediating ? (
                          <LoaderCircle className="spin" size={14} />
                        ) : (
                          <Send size={14} />
                        )}
                        提交修复
                      </button>
                    </div>
                    {fallbackValidated ? (
                      <small className="fallback-valid">
                        fallback Healthy · {validation.httpStatus ?? "—"} ·{" "}
                        {validation.latencyMs} ms
                      </small>
                    ) : null}
                    <small className="incident-current-target">
                      当前目标：{incident.qode.destination}
                    </small>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
